import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { subject } from '@casl/ability'
import { Form } from 'antd'
import { Forms } from 'components/Forms'
import { getCommunityInfo } from 'redux/store/chapterInfo/getters'
import { getUser } from 'redux/store/user/getters'
import { fetchUser } from 'redux/store/user/slice'
import { updateUserProfile } from './api'
import { TUserForm } from 'components/Forms/forms.user.d'
import { TUserProfile, UserData } from './types'
import { useAppDispatch } from '../../redux/createStore'
import RadioGroup from '../../components/RadioGroup/RadioGroup'
import { ACTIONS, ROLES, SUBJECTS, SubjectWithAttributes } from '../Permission'
import defineAbilityFor from '../Permission/ability'
import { routing, getSubtabs } from './constants'
import useRouter from '../../hooks/useRouter'
import { ContractCategoriesTable } from './ContractCategoriesTable'
import { useUserProfileContext } from './Providers/UserProfileContextProvider'
import { HospitalsTableContainer } from './HospitalsTable/HospitalsTableContainer'

export const UserProfile = ({ setActiveTab }: TUserProfile) => {
  const [isActive, setIsActive] = useState(false)

  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const user = useSelector(getUser)
  const communityInfo = useSelector(getCommunityInfo)

  const {
    state: { totalNumber }
  } = useUserProfileContext()

  const onSubmit = (data: TUserForm) => {
    const dataToSend: UserData = {
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data.phone_number || '',
      avatar_id: data?.avatar,
      timezone_id: data?.timezone_id,
      title: data?.title || null,
      vendor_preferences: data?.vendor_preferences || null
    }
    if ('hide_phone_number_for_vendor' in data) {
      dataToSend.hide_phone_number_for_vendor =
        data?.hide_phone_number_for_vendor
    }
    updateUserProfile(dataToSend).then(() => {
      dispatch(fetchUser())
      setIsActive(false)
    })
  }

  const { query, push } = useRouter()

  const ability = defineAbilityFor(user.role, user, '')

  const canSeeSubTabs = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.USER_CONTRACT_CATEGORIES_SETTINGS
  )

  const canSeeCommunityLevelCheckboxes = ability.can(
    ACTIONS.VIEW,
    subject(SUBJECTS.USER_CONTRACT_CATEGORIES_COMMUNITY_LEVEL_SETTINGS, {
      ...user
    } as unknown as SubjectWithAttributes)
  )

  const canSeeAssignmentStatus = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.USER_CONTRACT_CATEGORIES_ASSIGNMENT_STATUS_SETTINGS
  )

  const canViewVotingPanelMembership = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.VOTING_PANEL_MEMBERSHIP
  )

  const canUpdateVotingPanelMembership = ability.can(
    ACTIONS.UPDATE,
    SUBJECTS.VOTING_PANEL_MEMBERSHIP
  )

  const subTabsOptions = useMemo(() => {
    if (canSeeSubTabs) {
      if (user.role === ROLES.HOSPITAL_USER) {
        const total = user.hospitals?.reduce(
          (summ, hospital) => summ + hospital.contract_categories.length,
          0
        )
        return getSubtabs(total)
      }
      return getSubtabs(totalNumber)
    }
    return []
  }, [canSeeSubTabs, totalNumber, user])

  const [activeSubTab, setActiveSubTab] = useState(
    query?.type || subTabsOptions[0]?.value || routing.general
  )

  const handleTabs = (e) => {
    setActiveSubTab(e)
    push(e)
  }

  const userHospitals = useMemo(() => {
    return (
      user.hospitals?.map((hospital) => {
        return {
          name: hospital.name,
          uuid: hospital.hospital
        }
      }) ?? []
    )
  }, [user.hospitals])

  return (
    <div className="mb-24 column gap-24 space-between">
      {subTabsOptions.length > 0 && (
        <RadioGroup.Buttons
          options={subTabsOptions}
          defaultValue={activeSubTab}
          onChange={handleTabs}
          className="mt-0"
        />
      )}
      {activeSubTab === routing.general && (
        <Forms.User
          setActiveTab={setActiveTab}
          onSubmit={onSubmit}
          initialState={{
            ...user,
            community: communityInfo?.name,
            avatar: user?.avatar_logo?.file || null
          }}
          form={form}
          setIsActive={setIsActive}
          isActive={isActive}
          isProfile
        />
      )}
      {activeSubTab === routing.contract_categories &&
        (user.role === ROLES.HOSPITAL_USER ? (
          <HospitalsTableContainer
            hospitals={userHospitals}
            userId={user.uuid}
            canViewVotingPanelMembership={canViewVotingPanelMembership}
            canUpdateVotingPanelMembership={canUpdateVotingPanelMembership}
          />
        ) : (
          <ContractCategoriesTable
            userRole={user.role}
            canSeeCommunityLevelCheckboxes={canSeeCommunityLevelCheckboxes}
            canSeeAssignmentStatus={canSeeAssignmentStatus}
          />
        ))}
    </div>
  )
}
